import { ArrowRight, FileText, Frame, LayoutGrid, Mail, PlayCircle, Shapes } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function About() {
  return (
    <div>
      {/* Úvod: autorka se představuje, křídový portrét vpravo. Texty píše GPT (docs/reviews/010). */}
      <section className="mx-auto max-w-6xl px-4 pt-10 md:pt-14">
        <div className="md:grid md:grid-cols-[minmax(0,1fr)_300px] md:items-center md:gap-10">
          <div>
            <p className="mb-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-board">
              <span className="h-0.5 w-6 rounded bg-accent" />O autorce
            </p>
            <h1 className="text-[32px] leading-[1.1] md:text-[44px]">
              Učím matematiku a&nbsp;
              <span className="relative whitespace-nowrap">
                <span className="relative z-10">fyziku</span>
                <svg
                  className="absolute -bottom-1 left-0 z-0 h-[0.42em] w-full"
                  viewBox="0 0 200 24"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <path
                    d="M4 16 C 40 8, 90 20, 130 12 S 190 10, 197 14"
                    fill="none"
                    stroke="#F6C445"
                    strokeWidth={9}
                    strokeLinecap="round"
                    opacity={0.85}
                  />
                </svg>
              </span>
            </h1>
            <p className="mt-5 max-w-[52ch] text-lg text-ink-soft">
              Jmenuji se Petra Fischerová a&nbsp;učím matematiku a&nbsp;fyziku na 2. stupni základní
              školy. V&nbsp;Kabinetu nápadů najdete moje vlastní materiály k&nbsp;vytištění, které
              dětem pomáhají látku pochopit, procvičit a&nbsp;zároveň je baví.
            </p>
          </div>

          <figure className="mt-8 md:mt-0">
            <img
              src="/o-nas-ucitelka.webp"
              alt="Autorka materiálů, ilustrovaný portrét učitelky matematiky a fyziky"
              width={720}
              height={788}
              loading="lazy"
              decoding="async"
              className="mx-auto w-60 max-w-full md:w-72"
            />
            <figcaption className="mt-2 text-center text-sm text-ink-soft">
              Petra Fischerová, učitelka matematiky a&nbsp;fyziky
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Její příběh. */}
      <section className="mx-auto max-w-3xl px-4 py-8 md:py-12">
        <h2 className="text-2xl md:text-3xl">Jak to vzniklo</h2>

        <div className="mt-6 space-y-5 text-ink-soft">
          <p>
            Vlastní materiály jsem začala vytvářet, protože mi ke&nbsp;konkrétním hodinám chybělo
            přesně takové procvičování, jaké jsem pro své žáky potřebovala. Postupně přibývaly další
            a&nbsp;další.
          </p>
          <p>
            Nechtěla jsem si je nechávat jen pro sebe. Mohou pomoci i&nbsp;dalším učitelům, rodičům
            a&nbsp;dětem, a&nbsp;proto je dávám do&nbsp;Kabinetu nápadů.
          </p>
          <p>
            Největší radost mám ve&nbsp;chvíli, kdy dítě konečně pochopí, jak na to, a&nbsp;řekne:
            „Aha, už tomu rozumím." Právě takové materiály chci tvořit.
          </p>
        </div>
      </section>

      {/* Co v obchodě je. */}
      <section className="mx-auto max-w-3xl px-4 py-8 md:py-12">
        <h2 className="text-2xl md:text-3xl">Co tu najdete</h2>
        <p className="mt-5 text-ink-soft">
          V&nbsp;nabídce jsou materiály z&nbsp;fyziky a&nbsp;matematiky. Časem přibudou také
          videokurzy.
        </p>

        <div className="mt-6 space-y-6 text-ink-soft">
          <div className="flex gap-4">
            <Shapes className="mt-1 h-6 w-6 flex-none text-board" strokeWidth={2} />
            <p>
              <strong className="font-semibold text-ink">Výukové domino.</strong> Procvičování
              fyziky v&nbsp;podobě domina, které děti zapojí a&nbsp;pomůže jim zopakovat probíranou
              látku.
            </p>
          </div>

          <div className="flex gap-4">
            <FileText className="mt-1 h-6 w-6 flex-none text-board" strokeWidth={2} />
            <p>
              <strong className="font-semibold text-ink">Přehledové kartičky.</strong> Kartičky
              s&nbsp;řešenými úlohami, ke&nbsp;kterým se děti mohou vracet při procvičování
              i&nbsp;opakování.
            </p>
          </div>

          <div className="flex gap-4">
            <LayoutGrid className="mt-1 h-6 w-6 flex-none text-board" strokeWidth={2} />
            <p>
              <strong className="font-semibold text-ink">Výukové přehledy.</strong> Barevné
              přehledy učiva na&nbsp;formát A3 s&nbsp;definicemi, vzorci a&nbsp;obrázky,
              k&nbsp;výkladu, opakování i&nbsp;jako výzdoba učebny.
            </p>
          </div>

          <div className="flex gap-4">
            <Frame className="mt-1 h-6 w-6 flex-none text-board" strokeWidth={2} />
            <p>
              <strong className="font-semibold text-ink">Plakáty.</strong> Barevné plakáty
              s&nbsp;citáty vědců a&nbsp;matematiků k&nbsp;vytištění, hezká výzdoba do&nbsp;učebny
              nebo na&nbsp;chodbu.
            </p>
          </div>

          <div className="flex gap-4">
            <PlayCircle className="mt-1 h-6 w-6 flex-none text-board" strokeWidth={2} />
            <p>
              <strong className="font-semibold text-ink">Videokurzy.</strong> Časem nabídku doplním
              také o&nbsp;videokurzy z&nbsp;matematiky a&nbsp;fyziky.
            </p>
          </div>
        </div>
      </section>

      {/* Jak nákup probíhá + kontakt. */}
      <section className="mx-auto max-w-3xl px-4 pb-8 md:pb-12">
        <h2 className="text-2xl md:text-3xl">Než si něco vyberete</h2>
        <p className="mt-5 text-ink-soft">
          U&nbsp;každého materiálu najdete náhledy, takže předem uvidíte, co obsahuje. Zaplatit
          můžete QR kódem bez poplatku. Materiál vám během několika minut přijde
          e-mailem a&nbsp;můžete si ho vytisknout.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Link
            to="/"
            className="focus-ring group inline-flex min-h-[48px] items-center gap-2.5 rounded-xl bg-board px-6 py-3 font-semibold text-chalk transition hover:bg-board-deep active:scale-[.98]"
          >
            Vybrat materiál
            <ArrowRight
              className="h-[18px] w-[18px] transition-transform group-hover:translate-x-0.5"
              strokeWidth={2}
            />
          </Link>
        </div>

        <div className="mt-12 rounded-card border border-line bg-card p-6 shadow-card">
          <h2 className="text-xl">Napište mi</h2>
          <div className="mt-3 flex items-start gap-3">
            <Mail className="mt-1 h-5 w-5 flex-none text-board" strokeWidth={2} />
            <p className="text-ink-soft">
              Máte otázku k&nbsp;materiálům nebo objednávce? Napište mi na{' '}
              <a
                href="mailto:pf.kabinet.napadu@gmail.com"
                className="font-semibold text-board underline decoration-accent decoration-2 underline-offset-4 hover:text-board-deep"
              >
                pf.kabinet.napadu@gmail.com
              </a>
              .
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
